import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import {
  EnhancedSeoHead,
  FAQ,
  AuthorByline,
  bjornEvers,
} from "@appcaire/shared/seo";
import {
  generateArticleSchema,
  generateFAQSchema,
  generateBreadcrumbSchema,
  combineSchemas,
} from "@appcaire/shared/seo";
import { HelpCircle, ArrowRight } from "lucide-react";

/**
 * Static data hoisted outside component (React best practice: rendering-hoist-jsx)
 */
const VANLIGA_FRAGOR_FAQ_ITEMS = [
  {
    question: "Hur ansöker jag om hemtjänst i Nacka?",
    answer:
      "Du ansöker genom att kontakta Nacka kommuns biståndshandläggare. Ring 08-718 80 00 eller besök Nacka kommuns hemsida. En handläggare kommer att bedöma dina behov och besluta om vilken hjälp du har rätt till.",
  },
  {
    question: "Kan jag välja vilken hemtjänst jag vill ha?",
    answer:
      "Ja! Nacka har ett kundvalssystem som innebär att du själv väljer vilken anordnare som ska hjälpa dig. Du kan byta anordnare när du vill om du inte är nöjd.",
  },
  {
    question: "Vad kostar hemtjänst i Nacka?",
    answer:
      "Kostnaden beror på din inkomst och hur mycket hjälp du behöver. Det finns en maxtaxa som begränsar hur mycket du maximalt kan behöva betala. Kontakta kommunen för en personlig beräkning.",
  },
  {
    question: "Vad kan hemtjänsten hjälpa mig med?",
    answer:
      "Hemtjänsten kan hjälpa med personlig omvårdnad (dusch, påklädning), städning, tvätt, matlagning, inköp, promenader, socialt umgänge och mycket mer. Vilken hjälp du får beror på ditt biståndsbeslut.",
  },
  {
    question: "Hur snabbt kan jag få hemtjänst?",
    answer:
      "I akuta fall kan hjälpen starta redan samma dag. Normalt tar det några dagar till ett par veckor från ansökan till att hjälpen börjar, beroende på hur snabbt utredningen kan göras.",
  },
  {
    question: "Vem kan få hemtjänst i Nacka?",
    answer:
      "Hemtjänst kan tilldelas personer som har svårigheter att klara sig själva i hemmet på grund av ålder, sjukdom eller funktionsnedsättning. Biståndshandläggaren bedömer dina behov och bestämmer om du har rätt till hjälp.",
  },
  {
    question: "Kan jag få hemtjänst även om jag har anhöriga?",
    answer:
      "Ja, anhörigas hjälp räknas in i bedömningen, men det kan fortfarande finnas behov av hemtjänst. Biståndshandläggaren gör en helhetsbedömning baserat på dina faktiska behov.",
  },
  {
    question: "Hur byter jag anordnare?",
    answer:
      "Du kan byta anordnare när du vill genom att kontakta din biståndshandläggare eller direkt till den nya anordnaren. Det är viktigt att det finns en smidig övergång så att hjälpen fortsätter utan avbrott.",
  },
  {
    question: "Vad är skillnaden mellan hemtjänst och personlig assistans?",
    answer:
      "Hemtjänst är en kommunal service som bedöms och betalas av kommunen. Personlig assistans är en annan form av hjälp, ofta för personer med omfattande funktionsnedsättningar, som betalas av Försäkringskassan. Båda formerna av hjälp kan komplettera varandra.",
  },
  {
    question: "Kan jag få hemtjänst på helgerna?",
    answer:
      "Ja, många anordnare erbjuder service även på helger. Detta ingår ofta i det biståndsbeslut du har fått. Kontakta din anordnare för att diskutera behov av helgtjänster.",
  },
];

/**
 * VanligaFragor - FAQ page for nackahemtjanst.se
 *
 * Comprehensive FAQ page covering all common questions about home care in Nacka
 */
const VanligaFragor: React.FC = () => {
  /**
   * Gets the correct absolute path for links (always root-level for standalone app)
   */
  const getPath = (path: string) => path;

  // Memoize schema generation (React best practice: rerender-memo)
  const articleSchema = useMemo(
    () =>
      generateArticleSchema({
        headline: "Vanliga Frågor och Svar om Hemtjänst i Nacka",
        description:
          "Svar på de vanligaste frågorna om hemtjänst i Nacka kommun. Hitta svar på frågor om ansökan, kostnad, rättigheter och mer.",
        url: "https://nackahemtjanst.se/vanliga-fragor-och-svar",
        datePublished: "2025-12-22",
        dateModified: "2026-01-17",
      }),
    [],
  );

  const faqSchema = useMemo(
    () =>
      generateFAQSchema(
        VANLIGA_FRAGOR_FAQ_ITEMS.map((item) => ({
          question: item.question,
          answer: item.answer,
        })),
        "https://nackahemtjanst.se/vanliga-fragor-och-svar",
      ),
    [],
  );

  const breadcrumbSchema = useMemo(
    () =>
      generateBreadcrumbSchema([
        { name: "Hem", url: "https://nackahemtjanst.se/" },
        {
          name: "Vanliga frågor",
          url: "https://nackahemtjanst.se/vanliga-fragor-och-svar",
        },
      ]),
    [],
  );

  const combinedSchema = useMemo(
    () => combineSchemas([articleSchema, faqSchema, breadcrumbSchema]),
    [articleSchema, faqSchema, breadcrumbSchema],
  );

  return (
    <>
      <EnhancedSeoHead
        title="Vanliga Frågor och Svar om Hemtjänst i Nacka"
        description="Svar på de vanligaste frågorna om hemtjänst i Nacka kommun. Hitta svar på frågor om ansökan, kostnad, rättigheter och mer."
        canonicalUrl="https://nackahemtjanst.se/vanliga-fragor-och-svar"
        ogType="article"
        keywords={[
          "hemtjänst Nacka FAQ",
          "vanliga frågor hemtjänst Nacka",
          "frågor om hemtjänst Nacka",
          "hemtjänst Nacka svar",
          "hemtjänst Nacka information",
          "vanliga frågor och svar hemtjänst",
        ]}
        contentClassification="Consumer Portal"
        targetAudience="seniors, families"
        coverage="Sweden"
        publishDate="2025-12-22T00:00:00+01:00"
        modifiedDate="2026-01-17T00:00:00+01:00"
        author="Nacka Hemtjänst"
        schemaMarkup={combinedSchema ?? undefined}
        entities={[{ type: "Place", name: "Nacka" }]}
      />

      {/* Hero Section with Glass Effect */}
      <section className="relative bg-gradient-to-b from-purple-50 via-white to-white py-16 md:py-24 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-100/20 via-transparent to-blue-100/20" />
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <div className="bg-glass-white backdrop-blur-glass border border-glass-border rounded-2xl p-6 inline-block mb-6 shadow-glass-light">
            <HelpCircle className="w-16 h-16 text-purple-600" />
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-surface-900 tracking-tight mb-6">
            Vanliga Frågor och Svar
          </h1>
          <p className="text-xl md:text-2xl text-surface-600 max-w-3xl mx-auto mb-8 leading-relaxed">
            Här hittar du svar på de vanligaste frågorna om hemtjänst i Nacka
            kommun.
          </p>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          {/* Author Attribution (E-E-A-T) */}
          <AuthorByline
            author={bjornEvers}
            publishDate="2025-12-22"
            modifiedDate="2026-01-17"
          />

          <div className="mt-8">
            <FAQ
              items={VANLIGA_FRAGOR_FAQ_ITEMS}
              title="Vanliga frågor om hemtjänst i Nacka"
            />
          </div>
        </div>
      </section>

      {/* Related Links with Glass Effect */}
      <section className="py-16 px-4 bg-gradient-to-b from-white to-surface-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-surface-900 mb-8 text-center">
            Ytterligare information
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <Link
              to={getPath("/ansoka")}
              className="flex items-start gap-4 p-6 rounded-xl bg-glass-white backdrop-blur-glass border border-glass-border hover:bg-glass-hover transition-all shadow-glass-light"
            >
              <ArrowRight className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-surface-900 hover:text-emerald-600 transition-colors">
                  Ansök om hemtjänst
                </h3>
                <p className="text-sm text-surface-600">
                  Steg-för-steg guide för att ansöka hos Nacka kommun
                </p>
              </div>
            </Link>
            <Link
              to={getPath("/rattigheter")}
              className="flex items-start gap-4 p-6 rounded-xl bg-glass-white backdrop-blur-glass border border-glass-border hover:bg-glass-hover transition-all shadow-glass-light"
            >
              <ArrowRight className="w-5 h-5 text-purple-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-surface-900 hover:text-purple-600 transition-colors">
                  Dina rättigheter
                </h3>
                <p className="text-sm text-surface-600">
                  Läs mer om vad du har rätt till enligt lag
                </p>
              </div>
            </Link>
            <Link
              to={getPath("/hitta-hemtjanst")}
              className="flex items-start gap-4 p-6 rounded-xl bg-glass-white backdrop-blur-glass border border-glass-border hover:bg-glass-hover transition-all shadow-glass-light"
            >
              <ArrowRight className="w-5 h-5 text-blue-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-surface-900 hover:text-blue-600 transition-colors">
                  Hitta hemtjänst
                </h3>
                <p className="text-sm text-surface-600">
                  Jämför anordnare och hitta den som passar dig bäst
                </p>
              </div>
            </Link>
            <Link
              to={getPath("/kontakt")}
              className="flex items-start gap-4 p-6 rounded-xl bg-glass-white backdrop-blur-glass border border-glass-border hover:bg-glass-hover transition-all shadow-glass-light"
            >
              <ArrowRight className="w-5 h-5 text-amber-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-surface-900 hover:text-amber-600 transition-colors">
                  Kontakt
                </h3>
                <p className="text-sm text-surface-600">
                  Kontakta Nacka kommun direkt för personlig hjälp
                </p>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Disclaimer with Glass Effect */}
      <section className="py-12 px-4 bg-gradient-to-b from-surface-50 to-white">
        <div className="max-w-4xl mx-auto">
          <div className="bg-glass-white backdrop-blur-glass border border-glass-border rounded-2xl p-6 shadow-glass-light">
            <p className="text-sm text-surface-600 leading-relaxed">
              <strong>Viktig information:</strong> Denna webbplats är en
              oberoende guide och är inte en del av Nacka kommun. För officiell
              information och svar på dina frågor, kontakta alltid{" "}
              <a
                href="https://www.nacka.se"
                target="_blank"
                rel="noreferrer"
                className="text-emerald-600 font-medium hover:underline"
              >
                Nacka kommun
              </a>{" "}
              direkt.
            </p>
          </div>
        </div>
      </section>

      {/* Cross-link to hemtjanstguide.se */}
      <section className="py-12 px-4 bg-gradient-to-b from-white to-surface-50">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-glass-white backdrop-blur-glass border border-glass-border rounded-2xl p-6 shadow-glass-light">
            <p className="text-surface-600 mb-4">
              Letar du efter hemtjänst i andra kommuner? På{" "}
              <a
                href="https://hemtjanstguide.se"
                target="_blank"
                rel="noreferrer"
                className="text-emerald-600 font-medium hover:underline"
              >
                hemtjanstguide.se
              </a>{" "}
              hittar du information om hemtjänst i alla Sveriges kommuner.
            </p>
          </div>
        </div>
      </section>
    </>
  );
};

export default VanligaFragor;
